import React from 'react'
import { useSelector } from 'react-redux'
import { Box, Grid, Typography, Paper, List, ListItemButton, ListItemText, Divider, useTheme } from '@mui/material'
import AboutYou from './AboutYou'
import Security from './Security'

const EmployerUserProfile = () => {
  const employerUser = useSelector(state => state.employerUser)
  const [activeTab, setActiveTab] = React.useState(0)
  const theme = useTheme()

  const sections = [
    { label: 'About You', id: 'about-you', Component: <AboutYou employerUser={employerUser} /> },
    { label: 'Security', id: 'security', Component: <Security employerUser={employerUser} /> },
  ]

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      minHeight: 'calc(100vh - 64px)',
      mt: 0,
      background: theme.palette.background.default,
      pt: 0,
      pb: { xs: 3, md: 4 },
      px: 0
    }}>
      {/* Profile Header */}
      <Box sx={{
        width: '100%',
        backgroundColor: theme.palette.background.dark,
        borderRadius: '0px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        mb: 4,
        pt: 0,
        pb: { xs: 3, md: 4 },
        px: 0,
        mx: 0
      }}>
        <Box sx={{ maxWidth: '91.666667%', mx: 'auto', p: { xs: 2, md: 3 } }}>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                {employerUser.firstName}'s Profile
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0 }}>
                Manage your information and security settings
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* End Profile Header */}
      <Grid container spacing={4} sx={{ width: '100%', maxWidth: { xs: '100%', md: '1400px' }, mx: 'auto' }}>
        {/* Sidebar */}
        <Grid item xs={12} md={3} sx={{ width: '100%' }}>
          <Paper 
            elevation={0}
            sx={{ 
              position: 'sticky',
              top: 84,
              background: theme.palette.background.paper,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }
            }}
          >
            <Box sx={{ 
              p: 2.5, 
              borderBottom: '1px solid', 
              borderColor: 'divider',
              background: theme.palette.background.default
            }}>
              <Typography variant='h6' sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary
              }}>
                Menu
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {sections.map((section, index) => (
                <React.Fragment key={section.id}>
                  <ListItemButton
                    onClick={() => setActiveTab(index)}
                    selected={activeTab === index}
                    sx={{
                      py: 2,
                      px: 2.5,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: theme.palette.action.hover,
                        pl: 3
                      },
                      '&.Mui-selected': {
                        background: theme.palette.action.selected,
                        '&:hover': {
                          background: theme.palette.action.selected
                        }
                      }
                    }}
                  >
                    <ListItemText 
                      primary={section.label}
                      primaryTypographyProps={{
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      }}
                    />
                  </ListItemButton>
                  {index < sections.length - 1 && (
                    <Divider sx={{ opacity: 0.5 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9} sx={{ width: '100%' }}>
          <Box>
            <Paper 
              elevation={0} 
              sx={{ 
                width: '100%',
                minWidth: 0,
                mb: 4,
                p: { xs: 2.5, md: 4 },
                backgroundColor: 'background.paper',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant='h5' sx={{ 
                    color: 'text.primary',
                    fontWeight: 'bold',
                    mb: 1
                  }}>
                    Edit Your Profile
                  </Typography>
                  <Typography variant='body1' sx={{ 
                    color: 'text.secondary',
                    mb: { xs: 2, md: 0 },
                    whiteSpace: 'normal',
                    wordBreak: 'normal'
                  }}>
                    Update your personal information and security settings to manage your account effectively.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {sections[activeTab].Component}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default EmployerUserProfile
